import React, { useEffect, useRef } from 'react';
import { PropTypes } from 'prop-types';
import { useSelector } from 'react-redux';
import { useMatch, useParams } from 'react-router-dom';

import {
    useLazyGetCurrentUserQuery,
    useGetDocumentsQuery,
    useGetRolesQuery,
    useGetTunnelMessagesQuery,
} from 'api/main';
import { tunnelSelectors, userSelectors } from 'reducers';
import {
    useProClient,
    useSubscriptionDocument,
    useTunnelNavigation,
    useTunnelPhases,
} from 'utils/hooks';
import {
    stepIDs,
    skippableStepsForProClient,
    docTypes,
    subscriptionDocStatus,
    fundTypes,
} from 'utils/constants';

import { BigLoader, IneligibleDialog } from 'components';
import TunnelPhase from './TunnelPhase';
import TunnelEntry from '../tunnel-entry/TunnelEntry';
import TunnelContainer from './TunnelContainer';
import PhasesNav from '../PhasesNav';

const TunnelPage = props => {
    const {
        fundName,
        fundSubmitted,
        lastPhaseIndex,
        lastTunnelStepMemo,
        loggedIn,
        phaseMemo,
        roleChoice,
        roleName,
        roleSubmitted,
        setFundSubmitted,
        setRoleSubmitted,
        setSubmittedPedagogicalStep,
        submittedPedagogicalStep,
        tunnelStepMemo,
        tunnelSubscription,
    } = props;

    const mounted = useRef(false);
    const { stepId } = useParams();

    const { isLoading: rolesLoader } = useGetRolesQuery();
    const [getCurrentUser] = useLazyGetCurrentUserQuery();
    const { isFetching: messagesLoader } = useGetTunnelMessagesQuery();
    const { phases, isLoading: listLoader } = useTunnelPhases();

    const internalUser = useSelector(userSelectors.getInternalUser);
    const subscriptionFormStatus = useSelector(userSelectors.getSubscriptionFormStatus);
    const isCreatingSubscription = !!useMatch('/create-subscription/*');
    const currentInvestmentFund = useSelector(tunnelSelectors.getCurrentInvestmentFund);

    const isFCPR = currentInvestmentFund?.type === fundTypes.FCPR;
    const showTunnelEntry = isFCPR ? !submittedPedagogicalStep : !roleSubmitted;
    const subscriptionForm = useSubscriptionDocument(docTypes.SUBSCRIPTION_FORM);
    const navigateToStep = useTunnelNavigation();
    const isProClient = useProClient();

    const { data: bsDocuments = [] } = useGetDocumentsQuery(
        {
            type: docTypes.SUBSCRIPTION_FORM,
            subscriptionId: tunnelSubscription?.id,
            pagination: false,
        },
        {
            pollingInterval:
                subscriptionFormStatus === subscriptionDocStatus.GENERATING ? 5000 : undefined,
            skip:
                (loggedIn && tunnelSubscription?.indirectUntransferred) ||
                isCreatingSubscription ||
                !tunnelSubscription,
        }
    );

    useEffect(() => {
        const latestSubscriptionForm = bsDocuments[0];

        if (
            ((subscriptionForm?.id &&
                latestSubscriptionForm?.id &&
                subscriptionForm?.id !== latestSubscriptionForm?.id) || // old doc is different than the new doc
                (!subscriptionForm?.id && latestSubscriptionForm?.id)) && // first time generated
            subscriptionFormStatus === subscriptionDocStatus.GENERATING
        ) {
            getCurrentUser(true);
        }
    }, [bsDocuments, getCurrentUser, subscriptionForm, subscriptionFormStatus]);

    useEffect(() => {
        const fundName = tunnelSubscription?.investmentFund?.name;
        const investorQualification =
            tunnelSubscription?.investmentCharacteristics?.investorQualification;
        if (!(fundName && investorQualification)) return;
        const isSkippableStep = skippableStepsForProClient.includes(+stepId);

        if (isSkippableStep && isProClient) {
            navigateToStep(stepIDs.REGULATORY_DOCUMENTS);
        }
    }, [navigateToStep, stepId, tunnelSubscription, isProClient]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    return listLoader ||
        rolesLoader ||
        messagesLoader ||
        (!phaseMemo && submittedPedagogicalStep) ? (
        <BigLoader />
    ) : (
        <TunnelContainer>
            <PhasesNav phases={phases} currentPhase={phaseMemo} lastPhaseIndex={lastPhaseIndex} />
            <IneligibleDialog />
            {showTunnelEntry ? (
                <TunnelEntry
                    internalUser={internalUser}
                    fundSubmitted={fundSubmitted}
                    setFundSubmitted={setFundSubmitted}
                    fundName={fundName}
                    roleChoice={roleChoice}
                    setRoleSubmitted={setRoleSubmitted}
                    roleSubmitted={roleSubmitted}
                    submittedPedagogicalStep={submittedPedagogicalStep}
                    setSubmittedPedagogicalStep={setSubmittedPedagogicalStep}
                />
            ) : (
                !!phaseMemo && (
                    <TunnelPhase
                        roleName={roleName}
                        currentPhase={phaseMemo}
                        tunnelStepMemo={tunnelStepMemo}
                        lastTunnelStepMemo={lastTunnelStepMemo}
                        lastPhaseIndex={lastPhaseIndex}
                    />
                )
            )}
        </TunnelContainer>
    );
};
TunnelPage.propTypes = {
    tunnelSubscription: PropTypes.object,
};

export default TunnelPage;
